/**
 * Displays are screens (DEC-319, decided in `public/items-look-study.html` revs 3-9, built 2026-10-27): the pure
 * rules behind a Display's bezel label, its colour, its font ladder and its sizes. Words and colours are the study's
 * (`SRC_CAT`, `autoInk`, `FONT_FRAC`); change them in both places.
 */

import type { DisplaySourceType } from './projectStore.ts';
import type { InstrumentIconId } from '../components/InstrumentIcon.tsx';

export type DisplayLabelAt = 'tl' | 'tc' | 'tr' | 'bl' | 'bc' | 'br' | 'off';
export type DisplayAlign = 'left' | 'center';
export type DisplaySongAlign = 'left' | 'center' | 'right';

/** What each source shows, in plain words, for the label printed on the bezel: the KIND of thing (in that kind's
 *  colour), what it reads, and the kind's icon. "Now playing" alone did not say a timeline was showing; "Helix" alone
 *  did not say it was guitar effects (operator: "very clear as to what it's displaying, timeline or guitar fx etc"). */
export const DISPLAY_KIND: Record<DisplaySourceType, { kind: string; source: string; colour: string; icon: InstrumentIconId }> = {
  nowplaying:  { kind: 'Timeline',  source: 'Now playing',      colour: '#5aa2ff', icon: 'waveform' },
  timeline:    { kind: 'Timeline',  source: 'Launcher',         colour: '#5aa2ff', icon: 'waveform' },
  helix:       { kind: 'Guitar FX', source: 'Line 6 Helix',     colour: '#ff9a3d', icon: 'guitar-electric' },
  bpm:         { kind: 'Tempo',     source: 'BPM',              colour: '#5dd39e', icon: 'metronome' },
  timesig:     { kind: 'Tempo',     source: 'Time signature',   colour: '#5dd39e', icon: 'metronome' },
  transport:   { kind: 'Transport', source: 'Live',             colour: '#5dd39e', icon: 'waveform' },
  recquant:    { kind: 'Ableton',   source: 'Rec quantize',     colour: '#f1a70a', icon: 'metronome' },
  launchquant: { kind: 'Ableton',   source: 'Launch quantize',  colour: '#f1a70a', icon: 'metronome' },
  metronome:   { kind: 'Ableton',   source: 'Metronome',        colour: '#f1a70a', icon: 'metronome' },
  lfo:         { kind: 'LFO',       source: 'LFO',              colour: '#ca8cfe', icon: 'waveform' },
  button:      { kind: 'Button',    source: 'Button',           colour: '#ff4040', icon: 'midi' },
  transpose:   { kind: 'Transpose', source: 'concert pitch',    colour: '#ffffff', icon: 'piano' },
  capo:        { kind: 'Capo',      source: 'guitar',           colour: '#ffd400', icon: 'guitar-acoustic' },
  key:         { kind: 'Tune',      source: 'Waves key',        colour: '#00d5ff', icon: 'vocals' },
};

/** Font S..XL as shares of the display's height (at M, 130 px tall: 14 / 20 / 26 / 34). Auto is as big as fits and
 *  never passes XL (operator: "on the L size, the font auto font is bigger than the XL"). */
export const DISPLAY_FONT_FRAC = { S: 0.11, M: 0.155, L: 0.2, XL: 0.262 } as const;

export function displayL1Max(h: number, nameSize: 'auto' | 'S' | 'M' | 'L' | 'XL' | undefined): number {
  return Math.round(h * DISPLAY_FONT_FRAC[!nameSize || nameSize === 'auto' ? 'XL' : nameSize]);
}
